import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import BidDetailsForm from './BidDetailsForm';
import FileUploadForm from './FileUploadForm';
import EmailNotificationForm from './EmailNotificationForm';
import DotWave from 'src/components/DotWave';

// Interfaces
export interface BidFormData {
  bidType: string;
  contractNo: string;
  title: string;
  categories: string[];
  department: string;
  adDate: Date | null;
  openingDate: Date | null;
  effectiveDate: Date | null;
  status: string;
  description: string;
  adminNotes: string;
  awardedToVendor?: string;
  awardedToContact?: string;
  awardAmount?: string;
  poNumber: string;
  awardedToContactId: number;
}

export interface BidFormErrors {
  bidType?: string;
  contractNo?: string;
  title?: string;
  categories?: string;
  department?: string;
  adDate?: string;
  openingDate?: string;
  status?: string;
  saveType?: string;
  subject?: string;
  awardedToVendor?: string;
  awardedToContact?: string;
}

export interface UploadedFile {
  title: string;
  file: File | null;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  id?: string;
  tempId?: string;
  isUploading?: boolean;
  uploaded?: boolean;
  uploadError?: string;
}

export interface EmailNotification {
  subject: string;
  content: string;
  saveType: string;
}

// Dropdown data interfaces
export interface TypeOption {
  TypeID: number;
  Type: string;
  Active: boolean;
}

export interface CategoryOption {
  CategoryID: number;
  CategoryName: string;
}

export interface DepartmentOption {
  DepartmentID: number;
  DepartmentName: string;
}

export interface StatusOption {
  StatusID: number;
  Status: string;
  Active: boolean;
}

const BidCreationForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state: locationState, township, uuid } = useParams();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingContractNo, setIsGeneratingContractNo] = useState(false);
  const [fileStepValid, setFileStepValid] = useState(false);

  // State for API data
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  const [formData, setFormData] = useState<BidFormData>({
    bidType: '',
    contractNo: '',
    title: '',
    categories: [],
    department: '',
    adDate: null,
    openingDate: null,
    effectiveDate: null,
    status: null,
    description: '',
    adminNotes: '',
    awardedToVendor: '',
    awardAmount: null,
    poNumber: '',
    awardedToContactId: null
  });

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([
    {
      title: 'Bid Document',
      file: null
    }
  ]);

  const [emailData, setEmailData] = useState<EmailNotification>({
    subject: '',
    content: '',
    saveType: ''
  });

  const [errors, setErrors] = useState<BidFormErrors>({});

  const steps = ['Bid Details', 'Upload Files', 'Email Notification'];

  const fetchDropdownData = async () => {
    setLoadingDropdowns(true);
    try {
      // Fetch types
      const typesResponse = await httpRequest(
        'GET',
        `/bid_portal/type/get_all`,
        {},
        {}
      );

      if (typesResponse.status === 'success') {
        const typesData =
          typesResponse.data?.data || typesResponse.data?.Data || [];
        setTypes(typesData);
      } else {
        console.error('Failed to fetch types:', typesResponse);
      }

      // Fetch categories
      const categoriesResponse = await httpRequest(
        'GET',
        `/bid_portal/category/get_all`,
        {},
        {}
      );

      if (categoriesResponse.status === 'success') {
        const categoriesData =
          categoriesResponse.data?.data || categoriesResponse.data?.Data || [];
        setCategories(categoriesData);
      } else {
        console.error('Failed to fetch categories:', categoriesResponse);
      }

      // Fetch departments
      const departmentsResponse = await httpRequest(
        'GET',
        '/bid_portal/department/get_all',
        {},
        {}
      );

      if (departmentsResponse.status === 'success') {
        const departmentsData =
          departmentsResponse.data?.data ||
          departmentsResponse.data?.Data ||
          [];
        setDepartments(departmentsData);
      } else {
        console.error('Failed to fetch departments:', departmentsResponse);
      }

      // Fetch statuses
      const statusesResponse = await httpRequest(
        'GET',
        `/bid_portal/status/get_all`,
        {},
        {}
      );

      if (statusesResponse.status === 'success') {
        const statusesData =
          statusesResponse.data?.data || statusesResponse.data?.Data || [];
        setStatuses(statusesData);
      } else {
        console.error('Failed to fetch statuses:', statusesResponse);
      }
    } catch (errFetch) {
      console.error('Error fetching dropdown data:', errFetch);
      setError('Failed to load form options. Please try again.');
    } finally {
      setLoadingDropdowns(false);
    }
  };
  // Fetch all dropdown data
  useEffect(() => {
    fetchDropdownData();

    // set ad and opening date autofill
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Round current time up to the next 5-minute interval
    const now = new Date();
    const minutes = now.getMinutes();
    const remainder = minutes % 5;
    if (remainder !== 0) {
      now.setMinutes(minutes + (5 - remainder)); // round up
    }
    now.setSeconds(0);
    now.setMilliseconds(0);
    setFormData({ ...formData, adDate: today, openingDate: now });
  }, []);

  // Handle bid type change and auto-generate contract number
  const handleBidTypeChange = async (selectedType: string, typeId: number) => {
    // First update the form data with the selected type
    setFormData({ ...formData, bidType: selectedType });

    // Find the TypeID for the selected type
    const selectedTypeObj = types.find((type) => type.Type === selectedType);

    if (selectedTypeObj?.TypeID || typeId) {
      try {
        // Show loading state
        setIsGeneratingContractNo(true);
      } catch (err: any) {
        console.error('Error generating contract number:', err);
        toast.error(err.message || 'Failed to generate contract number');
      } finally {
        setIsGeneratingContractNo(false);
      }
    }
  };

  const validateForm = () => {
    const newErrors: BidFormErrors = {};
    // Get the current date/time and add one minute
    const currentDateTime = new Date();
    currentDateTime.setMinutes(currentDateTime.getMinutes() - 1); // Add one minute
    currentDateTime.setSeconds(0); // Set seconds to 0 to avoid any issues with selecting seconds
    currentDateTime.setMilliseconds(0); // Set milliseconds to 0

    // Step 1: Bid details validation
    if (activeStep === 0) {
      if (!formData.bidType) newErrors.bidType = 'Type is required';
      if (!formData.title) newErrors.title = 'Title is required';
      if (!formData.contractNo)
        newErrors.contractNo = 'Contract No. is required';
      if (formData.categories.length === 0)
        newErrors.categories = 'At least one category is required';
      if (!formData.department) newErrors.department = 'Department is required';
      if (!formData.openingDate) {
        newErrors.openingDate = 'Opening Date is required';
      } else if (formData.openingDate < currentDateTime) {
        newErrors.openingDate = 'Opening Date must be in the future';
      }
      if (!formData.adDate) newErrors.adDate = 'Ad Date is required';
      if (!formData.status) newErrors.status = 'Status is required';

      if (formData.status === 'Awarded') {
        if (!formData.awardedToVendor)
          newErrors.awardedToVendor = 'Awarded To (Vendor) is required';
        if (!formData.awardedToContact)
          newErrors.awardedToContact = 'Awarded To (Contact) is required';
      }
    }

    // Step 3: Email Notification validation
    if (activeStep === 2) {
      if (!emailData.subject) newErrors.subject = 'Email subject is required';
      if (!emailData.saveType)
        newErrors.saveType = 'Notification option is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to validate file upload step
  const validateUploadStep = (isValid: boolean) => {
    setFileStepValid(isValid);
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateForm()) {
      return; // Prevent progression to next step if step 1 is not valid
    }

    const hasUploadError = uploadedFiles.some((item) => item.file === null);

    if (activeStep === 1 && hasUploadError) {
      return;
    }
    if (activeStep === 1 && !fileStepValid) {
      // setError('At least one document is required before proceeding');
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFileUpload = async (index: number, file: File) => {
    // Update UI to show selected file immediately
    const newFiles = [...uploadedFiles];
    const currentFile = newFiles[index];

    // Set loading state for this specific file
    newFiles[index] = {
      ...currentFile,
      file,
      uploaded: true,
      uploadError: undefined
    };
    setUploadedFiles(newFiles);

    // Clear any error messages after successful upload
    setError(null);

    // Update file step validation
    setFileStepValid(true);
  };

  const addFileUpload = () => {
    setUploadedFiles([
      ...uploadedFiles,
      {
        title: `Bid Document`,
        file: null
      }
    ]);
  };

  const removeFileUpload = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);

    // Check if we still have any uploaded files after removal
    const hasUploadedFile = newFiles.some((file) => file.uploaded || file.file);
    setFileStepValid(hasUploadedFile);

    setError(null);
  };

  // New function to remove an uploaded document
  const removeUploadedDocument = async (index: number): Promise<void> => {
    return new Promise<void>((resolve) => {
      // Get the file to be removed
      const fileToRemove = uploadedFiles[index];

      // Only proceed if there's an uploaded file
      if (!fileToRemove.uploaded || !fileToRemove.fileName) {
        toast.error('No uploaded document to remove');
        resolve();
        return;
      }

      // Determine the default title for this position
      const defaultTitle =
        index === 0 ? 'Original Package' : `Attachment ${index}`;

      // Update the file status in the state
      const updatedFiles = [...uploadedFiles];
      updatedFiles[index] = {
        ...updatedFiles[index],
        title: defaultTitle, // Reset to default title
        file: null,
        fileUrl: undefined,
        fileName: undefined,
        fileType: undefined,
        isUploading: false,
        uploaded: false,
        uploadError: undefined
      };

      setUploadedFiles(updatedFiles);

      // Check if we still have any uploaded files after removal
      const hasUploadedFile = updatedFiles.some(
        (file) => file.uploaded || file.file
      );
      setFileStepValid(hasUploadedFile);

      toast.success('Document removed successfully');
      resolve();
    });
  };

  const handleSubmit = async (isDraft: boolean) => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Find the corresponding IDs for selected values
      const typeID = types.find((t) => t.Type === formData.bidType)?.TypeID;
      const foundStatus = statuses.find((t) => t.Status === formData.status);

      const departmentID = departments.find(
        (d) => d.DepartmentName === formData.department
      )?.DepartmentID;

      // Find CategoryIDs by names
      const categoryIDs = formData.categories
        .map((catName) => {
          const category = categories.find((c) => c.CategoryName === catName);
          return category ? category.CategoryID : null;
        })
        .filter((id) => id !== null) as number[];

      // Check if any files are still uploading
      const hasUploadingFiles = uploadedFiles.some((file) => file.isUploading);
      if (hasUploadingFiles) {
        setError(
          'Please wait for all file uploads to complete before submitting'
        );
        setLoading(false);
        return;
      }

      // Check for any upload errors
      const hasUploadErrors = uploadedFiles.some((file) => file.uploadError);
      if (hasUploadErrors) {
        const confirmContinue = window.confirm(
          'Some files have upload errors. Do you want to continue with the submission anyway?'
        );
        if (!confirmContinue) {
          setLoading(false);
          return;
        }
      }

      // Prepare normal JSON body
      const bidPayload = {
        TypeID: typeID,
        ContractNo: formData.contractNo,
        Title: formData.title,
        CategoryIDs: categoryIDs,
        DepartmentID: departmentID,
        AdDate: formData.adDate ? formData.adDate.toISOString() : null,
        OpeningDate: formData.openingDate
          ? formData.openingDate.toISOString()
          : null,
        EffectiveDate: formData.effectiveDate
          ? formData.effectiveDate.toISOString()
          : null,
        StatusID: foundStatus.StatusID,
        Description: formData.description,
        AdminNote: formData.adminNotes,
        DocumentFileUpload: uploadedFiles.map((file) => ({
          Type: file.fileType || file.file?.type || '',
          FileName: file.fileName || file.file?.name || '',
          Description: 'Bid Document'
        })),
        SendNotifyProcess: emailData.saveType || 'Save',
        EmailSubject: emailData.subject,
        EmailBody: emailData.content,
        IsSaveDraft: isDraft,
        ...(formData.status === 'Awarded' && {
          AwardedTo: formData.awardedToContactId,
        }),
        ...(formData.awardAmount ? { AwardAmount: formData.awardAmount } : {}),
        
      };

      // ----------------------
      // Create FormData
      // ----------------------
      const formDataToSend = new FormData();

      // Add JSON as string
      formDataToSend.append('data', JSON.stringify(bidPayload));

      // Add all files
      uploadedFiles.forEach((fileItem, index) => {
        if (fileItem.file) {
          formDataToSend.append('DocumentFileUpload', fileItem.file); // file
          formDataToSend.append(
            `DocumentDescription[${index}]`,
            'Bid Document'
          ); // meta field
          formDataToSend.append(`DocumentType[${index}]`, fileItem.file.type);
          formDataToSend.append(`DocumentName[${index}]`, fileItem.file.name);
        }
      });

      // ----------------------
      // Send SINGLE API call
      // ----------------------
      const response = await httpRequest(
        'POST',
        '/bid_portal/bid_details/add',
        formDataToSend,
        { 'Content-Type': 'multipart/form-data' }
      );

      if (response.type === 'NETWORK_ERROR' || response.status !== 'success') {
        throw new Error(response.message || 'Failed to create bid');
      }

      toast.success('Bid created successfully');
      navigate(`/${locationState}/${township}/${uuid}/bid-portal/Admin/home`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <BidDetailsForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            types={types}
            categories={categories}
            departments={departments}
            statuses={statuses}
            isGeneratingContractNo={isGeneratingContractNo}
            handleBidTypeChange={handleBidTypeChange}
            fetchDropdownData={fetchDropdownData}
          />
        );
      case 1:
        return (
          <FileUploadForm
            setUploadedFiles={setUploadedFiles}
            uploadedFiles={uploadedFiles}
            handleFileUpload={handleFileUpload}
            addFileUpload={addFileUpload}
            removeFileUpload={removeFileUpload}
            setError={setError}
            validateUploadStep={validateUploadStep}
            removeUploadedDocument={removeUploadedDocument}
          />
        );
      case 2:
        return (
          <EmailNotificationForm
            emailData={emailData}
            setEmailData={setEmailData}
            goLiveDate={null}
            errors={errors}
            setErrors={setErrors}
          />
        );
      default:
        return null;
    }
  };

  if (loadingDropdowns) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4 }}>
      <Typography
        mb={2}
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: theme.palette.primary.main,
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 80,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.primary.main
          }
        }}
      >
        Create Bid
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, mt: 3 }}>
          {error}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => handleSubmit(false)}
            disabled={loading || uploadedFiles.some((file) => file.isUploading)}
            sx={{
              minWidth: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textTransform: 'none',
              paddingY: '10px'
            }}
          >
            {loading ? <DotWave /> : 'Submit'}
          </Button>
        )}

        {/* {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={() => handleSubmit(true)}
            disabled={
              loading ||
              formData?.goLiveDate instanceof Date ||
              uploadedFiles.some((file) => file.isUploading)
            }
          >
            {loading ? <CircularProgress size={24} /> : 'Save Draft'}
          </Button>
        )} */}
      </Box>
    </Paper>
  );
};

export default BidCreationForm;
